import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import type { Order } from "@/types/order";

export interface CustomerDetailData {
  id: string;
  name: string;
  phone: string | null;
  normalized_phone: string | null;
  internal_note: string | null;
  delivered_count: number;
  total_revenue: string;
  last_delivered_at: string | null;
  recent_addresses: string[];
}

export function useCustomerDetail(customerId: string | undefined) {
  const { user } = useAuth(false);

  const detailQuery = useQuery({
    queryKey: ["customer-detail", customerId],
    queryFn: async (): Promise<CustomerDetailData | null> => {
      if (!customerId) return null;

      const { data, error } = await supabase.rpc("get_customer_detail", {
        p_customer_id: customerId,
      });

      if (error) {
        console.error("Error fetching customer detail:", error);
        throw error;
      }

      if (!data || data.length === 0) return null;
      return data[0] as CustomerDetailData;
    },
    enabled: !!user?.id && !!customerId,
    staleTime: 30 * 1000,
  });

  const ordersQuery = useQuery({
    queryKey: ["customer-orders", customerId],
    queryFn: async (): Promise<Order[]> => {
      if (!customerId) return [];

      const { data, error } = await supabase
        .from("orders")
        .select("*")
        .eq("customer_id", customerId)
        .order("created_at", { ascending: false })
        .limit(20);

      if (error) {
        console.error("Error fetching customer orders:", error);
        throw error;
      }

      return (data || []) as Order[];
    },
    enabled: !!user?.id && !!customerId,
    staleTime: 30 * 1000,
  });

  return {
    customer: detailQuery.data ?? null,
    customerLoading: detailQuery.isLoading,
    orders: ordersQuery.data || [],
    ordersLoading: ordersQuery.isLoading,
  };
}
